/**
 * Export Context Menu Service
 *
 * Adds right-click context menu commands to the Scene Explorer tree
 * for exporting a selected node (and its descendants) in various formats,
 * and for uploading a node directly to the user's catalog folder.
 */

import {
    SceneExplorerServiceIdentity,
    type ServiceDefinition,
    type ISceneExplorerService,
} from '@babylonjs/inspector';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { TraceViewerElement } from 'trace-viewer';
import type { SceneObject, ZipProgress } from 'trace-viewer/scene';
import { getCatalogToken, getCatalogSelectedFolderId, LS_TEAM_ID_KEY, apiConfig } from './catalog-shared';
import { captureSceneThumbnail } from 'trace-viewer/editor';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { EntityType, Assets } from 'trace-viewer/cms';
import { withContentStorage } from './cms/asset-writes';
import { showToast, showSpinnerToast } from './toast';
import { exportWithProgressToast } from './utils/export-toast';
import { BUNDLE_EXTENSION } from './scene-formats';
import { includesEditorRoot, subtreeExclusion } from './editor-content';

const getViewer = (): TraceViewerElement | null =>
    document.querySelector('trace-viewer');

/**
 * Resolve a TransformNode entity from the scene explorer to its SceneObject.
 * Returns null if the node doesn't map to a SceneObject.
 */
export function resolveSceneObject(entity: TransformNode): SceneObject | null {
    const viewer = getViewer();
    if (!viewer?.traceScene) return null;

    const sceneObjects = viewer.traceScene.getSceneObjects();

    // Direct transform match
    for (const so of sceneObjects) {
        if (so.transform === entity) return so;
    }

    // Walk up parents to find the owning SceneObject
    let current = entity.parent;
    while (current) {
        for (const so of sceneObjects) {
            if (so.transform === current) return so;
        }
        current = current.parent;
    }

    return null;
}

export const ExportContextMenuServiceDefinition: ServiceDefinition<[], [ISceneExplorerService]> = {
    friendlyName: 'Export Context Menu',
    consumes: [SceneExplorerServiceIdentity],

    factory: (sceneExplorerService: ISceneExplorerService) => {
        const predicate = (entity: unknown): entity is TransformNode =>
            entity instanceof TransformNode && resolveSceneObject(entity) !== null;

        const runSubtreeExport = (
            entity: TransformNode,
            ext: string,
            getBlob: (
                viewer: TraceViewerElement,
                so: SceneObject,
                onProgress: (p: ZipProgress) => void,
            ) => Promise<Blob>,
        ): void => {
            const viewer = getViewer();
            const so = resolveSceneObject(entity);
            if (!viewer || !so) return;
            void exportWithProgressToast(`${so.name}${ext}`, (onProgress) =>
                getBlob(viewer, so, onProgress),
            );
        };

        const glzCmd = sceneExplorerService.addEntityCommand<TransformNode>({
            order: 102,
            predicate,
            getCommand: (entity) => ({
                displayName: 'Export as .glz',
                mode: 'contextMenu' as const,
                type: 'action' as const,
                execute: () => runSubtreeExport(entity, BUNDLE_EXTENSION, (v, so, onProgress) => v.exportSubtreeToGlzBlob(so, { onProgress, ...subtreeExclusion(v.traceScene, so) })),
            }),
        });

        const glbCmd = sceneExplorerService.addEntityCommand<TransformNode>({
            order: 103,
            predicate,
            getCommand: (entity) => ({
                displayName: 'Export as .glb',
                mode: 'contextMenu' as const,
                type: 'action' as const,
                execute: () => runSubtreeExport(entity, '.glb', (v, so) => {
                    // The .glb writer takes no exclusion, so the Editor group
                    // (the AI conversation) would go into the download.
                    if (includesEditorRoot(v.traceScene, so)) {
                        throw new Error('A .glb cannot leave out the Editor group; export a subtree that does not include it.');
                    }
                    return v.exportObjectToGlbBlob(so);
                }),
            }),
        });

        const catalogCmd = sceneExplorerService.addEntityCommand<TransformNode>({
            order: 104,
            predicate,
            getCommand: (entity) => ({
                displayName: 'Add to Catalog',
                mode: 'contextMenu' as const,
                type: 'action' as const,
                execute: () => {
                    const viewer = getViewer();
                    const so = resolveSceneObject(entity);
                    if (!viewer || !so) return;
                    addToCatalog(viewer, so).catch((err: unknown) =>
                        console.error('[Export Context Menu] Add to Catalog failed:', err)
                    );
                },
            }),
        });

        return {
            dispose: () => {
                glzCmd.dispose();
                glbCmd.dispose();
                catalogCmd.dispose();
            },
        };
    },
};

/**
 * Export a SceneObject as .trace, capture a thumbnail, and upload
 * to the currently selected catalog folder.
 */
async function addToCatalog(viewer: TraceViewerElement, sceneObject: SceneObject): Promise<void> {
    const token = getCatalogToken();
    if (!token) {
        showToast('Sign in to the catalog first', 'error');
        return;
    }

    const folderId = getCatalogSelectedFolderId();
    if (!folderId) {
        showToast('Open a folder in the catalog first', 'error');
        return;
    }

    const teamId = localStorage.getItem(LS_TEAM_ID_KEY);
    if (!teamId) {
        showToast('No team selected', 'error');
        return;
    }

    const name = sceneObject.name;
    const dismiss = showSpinnerToast(`Adding "${name}" to catalog...`);

    try {
        // Export subtree as .trace blob
        const sceneBlob = await viewer.exportSubtreeToBlob(sceneObject, { zipFormat: 'trace', ...subtreeExclusion(viewer.traceScene, sceneObject) });
        const file = new File([sceneBlob], `${name}.trace`, { type: 'application/octet-stream' });

        // Capture thumbnail using RTT (square aspect, transparent bg, no grid)
        const scene = viewer.scene;
        const cam = scene?.activeCamera;
        const thumbnail = scene && cam instanceof ArcRotateCamera
            ? await captureSceneThumbnail(scene, cam)
            : undefined;

        // Upload to CMS
        const assetsClient = new Assets(apiConfig(token));
        await assetsClient.assetsCreate(withContentStorage({
            FolderId: folderId,
            TeamId: teamId,
            File: file,
            Thumbnail: thumbnail,
            Title: name,
            EntityType: EntityType.Trace,
        }));

        dismiss(`Added "${name}" to catalog`, 'success');

        // Signal the catalog panel to refresh
        window.dispatchEvent(new CustomEvent('trace-catalog:folder-refresh'));
    } catch (err) {
        console.error('[Export Context Menu] Catalog upload failed:', err);
        const msg = err instanceof Error ? err.message : String(err);
        dismiss(`Upload failed: ${msg}`, 'error');
    }
}
